import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { Modal } from '../ui/Modal';
import { polishDraft, type PolishMode } from './api';

/**
 * The clock and the two pens (Phase 146).
 *
 * **The clock** replaces a date field that sat in the composer all day for the one message a week
 * that is scheduled — it took the width of two buttons to do nothing. Now: type the message, press
 * the clock, choose when.
 *
 * **The pens** are a correction and a rewrite, and they are deliberately two buttons rather than
 * one. "Corrija" and "melhore" are different requests: somebody who asked for a spell check and
 * got their message rewritten stops pressing the button, and they are right to.
 *
 * Nothing here sends. A rewrite is offered beside what was typed and replaces it only when
 * somebody says so — the alternative is a product that edits your words behind your back.
 */

export function ScheduleButton({
  disabled,
  value,
  onChange,
  onSchedule,
  pending,
}: {
  disabled: boolean;
  value: string | null;
  onChange: (next: string | null) => void;
  onSchedule: () => void;
  pending: boolean;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        className={`button button--quiet button--compact${value ? ' is-armed' : ''}`}
        onClick={() => setOpen(true)}
        title={t('inbox.scheduleFor')}
        aria-label={t('inbox.scheduleFor')}
        disabled={disabled}
      >
        <ClockIcon />
      </button>

      <Modal
        open={open}
        title={t('inbox.scheduleFor')}
        description={t('inbox.scheduleHint')}
        onClose={() => setOpen(false)}
        footer={
          <>
            {value && (
              <button
                type="button"
                className="button button--quiet"
                onClick={() => {
                  onChange(null);
                  setOpen(false);
                }}
              >
                {t('inbox.scheduleClear')}
              </button>
            )}

            <button type="button" className="button button--quiet" onClick={() => setOpen(false)}>
              {t('confirm.cancel')}
            </button>

            <button
              type="button"
              className="button button--primary"
              disabled={!value || pending}
              onClick={() => {
                onSchedule();
                setOpen(false);
              }}
            >
              {pending ? t('inbox.scheduling') : t('inbox.schedule')}
            </button>
          </>
        }
      >
        <label className="stack__field">
          <span>{t('inbox.scheduleWhen')}</span>
          {/*
           * The native field rather than a calendar of this product's own: it is the control
           * every phone and desktop already knows how to open, and a picker built here would be
           * one more thing to get wrong about timezones.
           */}
          <input
            type="datetime-local"
            className="input"
            autoFocus
            value={value ?? ''}
            onChange={(event) => onChange(event.target.value || null)}
          />
        </label>
      </Modal>
    </>
  );
}

export function PolishButtons({
  draft,
  disabled,
  onReplace,
}: {
  draft: string;
  disabled: boolean;
  onReplace: (text: string) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<PolishMode>('SPELLING');
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const ask = useMutation({
    mutationFn: (next: PolishMode) => polishDraft(draft, next),
    onSuccess: (answer) => {
      setError(null);
      setResult(answer.text);
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const start = (next: PolishMode): void => {
    setMode(next);
    setResult(null);
    setError(null);
    setOpen(true);
    ask.mutate(next);
  };

  return (
    <>
      <button
        type="button"
        className="button button--quiet button--compact"
        title={t('inbox.fixSpelling')}
        aria-label={t('inbox.fixSpelling')}
        disabled={disabled || draft.trim() === ''}
        onClick={() => start('SPELLING')}
      >
        <SpellIcon />
      </button>

      <button
        type="button"
        className="button button--quiet button--compact"
        title={t('inbox.improve')}
        aria-label={t('inbox.improve')}
        disabled={disabled || draft.trim() === ''}
        onClick={() => start('CLARITY')}
      >
        <WandIcon />
      </button>

      <Modal
        open={open}
        title={t(mode === 'SPELLING' ? 'inbox.fixSpelling' : 'inbox.improve')}
        description={t('inbox.polishHint')}
        onClose={() => setOpen(false)}
        wide
        footer={
          <>
            <button type="button" className="button button--quiet" onClick={() => setOpen(false)}>
              {t('inbox.keepMine')}
            </button>

            {/* A different request, not a retry: "mais argumentativa" is its own button. */}
            <button
              type="button"
              className="button button--quiet"
              disabled={ask.isPending}
              onClick={() => start('ARGUMENTATIVE')}
            >
              {t('inbox.moreArgumentative')}
            </button>

            <button
              type="button"
              className="button button--primary"
              disabled={!result || ask.isPending}
              onClick={() => {
                if (result) onReplace(result);
                setOpen(false);
              }}
            >
              {t('inbox.useThis')}
            </button>
          </>
        }
      >
        <div className="polish">
          <div className="polish__side">
            <span className="muted small">{t('inbox.yourText')}</span>
            <p className="modal__text">{draft}</p>
          </div>

          <div className="polish__side">
            <span className="muted small">{t('inbox.suggestionLabel')}</span>

            {ask.isPending ? (
              <p className="muted">{t('inbox.polishing')}</p>
            ) : result ? (
              <p className="modal__text">{result}</p>
            ) : (
              <p className="muted">—</p>
            )}
          </div>
        </div>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}
      </Modal>
    </>
  );
}

function ClockIcon(): React.JSX.Element {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function SpellIcon(): React.JSX.Element {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M4 18L9 6l5 12" />
      <path d="M6 14h6" />
      <path d="M16 15l2 2 4-4" />
    </svg>
  );
}

function WandIcon(): React.JSX.Element {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M15 4V2M15 10V8M12 6h2M18 6h2" />
      <path d="M4 20l9-9" />
      <path d="M11 7l6 6" />
    </svg>
  );
}
